'use client';

import { Step } from '../../../chat/types/base_step';
import { QuestionAnswers } from '../../../helpers/questions.helper';
import type React from 'react';
import { useEffect, useState } from 'react';
import { ArrowRight } from 'lucide-react';
import Tcpa from '../../tcpa';

interface FormProps {
  currentStep: Step;
  handleSubmit: (questionAnswers: QuestionAnswers[]) => void;
  answers: Record<string, any>;
  isStartSubmitted: boolean;
}

export default function Form({
  currentStep,
  handleSubmit: handleOptionSelect,
  answers,
  isStartSubmitted,
}: FormProps) {
  const [formData, setFormData] = useState<Record<string, string>>({});
  const [errors, setErrors] = useState<Record<string, string>>({});

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    let asjustedValue = value;
    if (name === 'phoneNumber') {
      asjustedValue = formatPhoneNumber(asjustedValue);
    }
    setFormData(prev => ({
      ...prev,
      [name]: asjustedValue,
    }));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    const newErrors: Record<string, string> = {};

    currentStep.attributes.forEach(attr => {
      const value = formData[attr.name] || '';

      if (attr.validations?.required && value.trim() === '') {
        newErrors[attr.name] = 'This field is required';
      } else if (
        attr.validations?.email &&
        !/^[\w-.]+@([\w-]+\.)+[\w-]{2,4}$/.test(value)
      ) {
        newErrors[attr.name] = 'Invalid email address';
      } else if (attr.validations?.phone && !isValidUSPhone(value)) {
        newErrors[attr.name] = 'Invalid phone number';
      } else if (
        attr.validations?.minLength &&
        value.length < attr.validations.minLength
      ) {
        newErrors[attr.name] = `Your input is too short`;
      } else if (
        attr.validations?.maxLength &&
        value.length > attr.validations.maxLength
      ) {
        newErrors[attr.name] = `Your input is too long`;
      }
    });

    setErrors(newErrors);
    const isFormInvalid = Object.keys(newErrors).length === 0;

    if (isFormInvalid) {
      let answers = currentStep.attributes.map(attr => ({
        attributeName: attr.name,
        value: formData[attr.name] ?? '',
      }));
      const trustedFormCertUrl = (
        window?.document?.querySelector(
          'input[name="xxTrustedFormCertUrl"]'
        ) as HTMLInputElement
      )?.value;

      const jornayaLeadId = (
        document.querySelector(
          'input[name="universal_leadid"]'
        ) as HTMLInputElement
      )?.value;

      if (jornayaLeadId != null) {
        answers.push({
          attributeName: 'leadJornayaToken',
          value: jornayaLeadId,
        });
      }
      if (trustedFormCertUrl != null) {
        answers.push({
          attributeName: 'trustedFormCertUrl',
          value: trustedFormCertUrl,
        });
      }
      handleOptionSelect(answers);
    }
  };

  useEffect(() => {
    const selectedValues = getSelectedValues(currentStep, answers);
    setFormData(selectedValues);
  }, [answers, currentStep]);

  return (
    <>
      <form onSubmit={handleSubmit} className="space-y-2">
        {currentStep.attributes.map(attr => (
          <div key={attr.name}>
            <label
              htmlFor={attr.name}
              className="block text-xs sm:text-sm font-medium text-gray-700 mb-1"
            >
              {attr.displayName || attr.name}
            </label>
            <input
              type={attr.name.includes('phoneNumber') ? 'tel' : 'text'}
              id={attr.name}
              name={attr.name}
              value={formData[attr.name] || ''}
              onChange={handleChange}
              maxLength={attr.name.includes('phoneNumber') ? 12 : undefined}
              className={`w-full p-2 sm:p-3 border ${
                errors[attr.name] ? 'border-red-500' : 'border-gray-300'
              } text-[16px] sm:text-sm rounded-md focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent transition-colors`}
            />
            {errors[attr.name] && (
              <p className="mt-1 text-xs sm:text-sm text-red-500">
                {errors[attr.name]}
              </p>
            )}
          </div>
        ))}
        <button
          type="submit"
          className="text-sm sm:text-xl w-full bg-emerald-600 text-white py-2 sm:py-3 px-4 rounded-md hover:bg-emerald-700 transition-colors flex items-center justify-center gap-2"
        >
          {isStartSubmitted ? (
            <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
          ) : (
            <>
              <span>Next</span>
              <ArrowRight className="h-5 w-5" />
            </>
          )}
        </button>
        {currentStep.tcpa === true && (
          <input type="hidden" name="universal_leadid" id="leadid_token" />
        )}
      </form>
      {currentStep.tcpa === true ? <Tcpa /> : null}
    </>
  );
}

function getSelectedValues(
  currentStep: Step,
  answers: Record<string, any>
): Record<string, string> {
  const selectedValues: Record<string, string> = {};

  currentStep.attributes.forEach(attr => {
    const value = answers[attr.name];
    if (value) {
      selectedValues[attr.name] = value;
    }
  });

  return selectedValues;
}

const formatPhoneNumber = (input: string) => {
  const numericInput = input.replace(/\D/g, '');
  let formattedPhoneNumber = numericInput.replace(
    /(\d{3})(\d{0,3})(\d{0,4})/,
    '$1-$2-$3'
  );
  if (formattedPhoneNumber.length == 0) return formattedPhoneNumber;

  while (formattedPhoneNumber[formattedPhoneNumber.length - 1] === '-') {
    formattedPhoneNumber = formattedPhoneNumber.slice(
      0,
      formattedPhoneNumber.length - 1
    );
  }
  return formattedPhoneNumber;
};

const isValidUSPhone = (value: string) =>
  /^(?:\+1\s?|1\s?)?(?:\([2-9][0-8][0-9]\)|[2-9][0-8][0-9])[-.\s]?[2-9][0-9]{2}[-.\s]?[0-9]{4}$/.test(
    value
  );
